import {
    AcceptRejectDto,
    CantCoverDto,
    ClosableCasesResponse,
    CoverOverlapDto,
    CreateDelegationDto,
    DelegationDto,
    DelegationEventDto,
    DelegationScope,
    EligibleCaseDto,
    ReassignDto,
    ReturnNoteDto,
    UnavailableSubstituteDto,
    UpdateDelegationDto,
} from 'types/delegations';
import { MinDto } from 'types/other';
import { portalAPI } from './portalAPI';

const APIDelegationsSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Delegation', 'Notifications'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getMyDelegations: builder.query<DelegationDto[], { scope: DelegationScope; status?: string }>({
                query: ({ scope, status }) => ({
                    url: `/v1/delegations`,
                    params: status ? { scope, status } : { scope },
                }),
                providesTags: ['Delegation'],
            }),
            getSubstituting: builder.query<DelegationDto[], { scope: DelegationScope }>({
                query: ({ scope }) => ({
                    url: `/v1/delegations/substituting`,
                    params: { scope },
                }),
                providesTags: ['Delegation'],
            }),
            getDelegation: builder.query<DelegationDto, { id: number }>({
                query: ({ id }) => `/v1/delegations/${id}`,
                providesTags: (_1, _2, arg) => [{ type: 'Delegation', id: arg.id }, 'Delegation'],
            }),
            getDelegationHistory: builder.query<DelegationEventDto[], { id: number }>({
                query: ({ id }) => `/v1/delegations/${id}/history`,
                providesTags: (_1, _2, arg) => [{ type: 'Delegation', id: arg.id }, 'Delegation'],
            }),
            // Without a delegator these are the caller's own; a coordinator names the lawyer they create for.
            getEligibleCases: builder.query<EligibleCaseDto[], { delegatorEmployeeId?: number }>({
                query: (params) => ({ url: `/v1/delegations/eligible-cases`, params }),
                keepUnusedDataFor: 0,
                // Closing a case (pre-flight) must drop it from the eligible list too.
                providesTags: ['Delegation'],
            }),
            getEligibleCaseCount: builder.query<{ count: number }, { delegatorEmployeeId?: number }>({
                query: (params) => ({ url: `/v1/delegations/eligible-cases/count`, params }),
                providesTags: ['Delegation'],
            }),
            // The people a view lists, whether or not they have a delegation: the Lawyer filter's options. Under TEAM,
            // also whom a coordinator may act for.
            getDelegationLawyers: builder.query<MinDto[], { scope: DelegationScope }>({
                query: ({ scope }) => ({ url: `/v1/delegations/lawyers`, params: { scope } }),
            }),
            getCloseableCases: builder.query<ClosableCasesResponse, void>({
                query: () => `/v1/delegations/closeable-cases`,
                providesTags: ['Delegation'],
            }),
            getUnavailableSubstitutes: builder.query<
                UnavailableSubstituteDto[],
                { startDate: string; endDate: string }
            >({
                query: ({ startDate, endDate }) => ({
                    url: `/v1/delegations/unavailable-substitutes`,
                    params: { startDate, endDate },
                }),
                // Availability shifts whenever a delegation is created/cancelled/returned/reassigned,
                // so refresh alongside the other delegation queries on those mutations.
                providesTags: ['Delegation'],
            }),
            // The covers the absence being planned would leave: the caller's own, or the lawyer's a coordinator names.
            getCoverOverlaps: builder.query<
                CoverOverlapDto[],
                { startDate: string; endDate: string; delegatorEmployeeId?: number }
            >({
                query: (params) => ({ url: `/v1/delegations/cover-overlaps`, params }),
                providesTags: ['Delegation'],
            }),
            createDelegation: builder.mutation<DelegationDto, CreateDelegationDto>({
                query: (body) => ({
                    url: `/v1/delegations`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['Delegation'],
            }),
            updateDelegation: builder.mutation<DelegationDto, { id: number } & UpdateDelegationDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/delegations/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }],
            }),
            cancelDelegation: builder.mutation<DelegationDto, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/delegations/${id}/cancel`,
                    method: 'POST',
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }],
            }),
            archiveDelegation: builder.mutation<DelegationDto, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/delegations/${id}/archive`,
                    method: 'PUT',
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }],
            }),
            acceptDelegation: builder.mutation<DelegationDto, { id: number } & AcceptRejectDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/delegations/${id}/accept`,
                    method: 'POST',
                    body,
                }),
                // 'Notifications' refetches the bell so the responder's dismissed invited-to-accept request
                // disappears immediately, without a page reload.
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }, 'Notifications'],
            }),
            rejectDelegation: builder.mutation<DelegationDto, { id: number } & AcceptRejectDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/delegations/${id}/reject`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }, 'Notifications'],
            }),
            returnDelegation: builder.mutation<DelegationDto, { id: number; early: boolean }>({
                query: ({ id, early }) => ({
                    url: `/v1/delegations/${id}/return`,
                    method: 'POST',
                    body: { early },
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }],
            }),
            addReturnNote: builder.mutation<DelegationDto, { id: number; caseId: number } & ReturnNoteDto>({
                query: ({ id, caseId, ...body }) => ({
                    url: `/v1/delegations/${id}/cases/${caseId}/return-note`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }],
            }),
            updateCaseHandoverNote: builder.mutation<
                void,
                { id: number; caseId: number; note: string; otherCaseIds?: number[] }
            >({
                query: ({ id, caseId, note, otherCaseIds }) => ({
                    url: `/v1/delegations/${id}/cases/${caseId}/handover-note`,
                    method: 'POST',
                    body: { note, otherCaseIds },
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }],
            }),
            reassignDelegation: builder.mutation<DelegationDto, { id: number } & ReassignDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/delegations/${id}/reassign`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }],
            }),
            cantCover: builder.mutation<DelegationDto, { id: number } & CantCoverDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/delegations/${id}/cases/cant-cover`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }, 'Notifications'],
            }),
            sendReminder: builder.mutation<DelegationDto, { id: number; substituteEmployeeId: number }>({
                query: ({ id, substituteEmployeeId }) => ({
                    url: `/v1/delegations/${id}/reminders`,
                    method: 'POST',
                    body: { substituteEmployeeId },
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }, 'Notifications'],
            }),
            /** Keeps the named new cases with the delegator, every new case when none is named. */
            keepNewCases: builder.mutation<DelegationDto, { id: number; matterIds?: number[] }>({
                query: ({ id, matterIds }) => ({
                    url: `/v1/delegations/${id}/kept-cases`,
                    method: 'POST',
                    body: { matterIds },
                }),
                invalidatesTags: (_1, _2, arg) => ['Delegation', { type: 'Delegation', id: arg.id }],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetMyDelegationsQuery,
    useLazyGetMyDelegationsQuery,
    useGetSubstitutingQuery,
    useLazyGetSubstitutingQuery,
    useGetDelegationQuery,
    useLazyGetDelegationQuery,
    useGetDelegationHistoryQuery,
    useGetEligibleCasesQuery,
    useLazyGetEligibleCasesQuery,
    useGetEligibleCaseCountQuery,
    useGetDelegationLawyersQuery,
    useGetCloseableCasesQuery,
    useLazyGetCloseableCasesQuery,
    useGetUnavailableSubstitutesQuery,
    useLazyGetUnavailableSubstitutesQuery,
    useGetCoverOverlapsQuery,
    useCreateDelegationMutation,
    useUpdateDelegationMutation,
    useCancelDelegationMutation,
    useArchiveDelegationMutation,
    useAcceptDelegationMutation,
    useRejectDelegationMutation,
    useReturnDelegationMutation,
    useAddReturnNoteMutation,
    useUpdateCaseHandoverNoteMutation,
    useReassignDelegationMutation,
    useCantCoverMutation,
    useSendReminderMutation,
    useKeepNewCasesMutation,
} = APIDelegationsSlice;
